import React, { useState, useEffect, memo, useCallback } from 'react';
import { Form, Input, Select, Upload, Card, Spin, message } from 'antd';
import { Button } from 'components';
import { SaveOutlined, ArrowLeftOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useItineraries from '../useItineraries';
import { Row, Col } from 'antd';
import { Editor } from '@tinymce/tinymce-react';
import { TranslateFormLabel } from 'components/translateInputAddon';

const { Option } = Select;

export const ItineraryForm = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [selectedShoppingCenter, setSelectedShoppingCenter] = useState(null);

  const { shoppingCenters, loadingCompanies, createItem, updateItem, fetchItem } = useItineraries();

  const isEditMode = !!id;

  const getAvailableLanguages = () => {
    if (!selectedShoppingCenter) return ['IT', 'EN'];
    const isSicilia =
      selectedShoppingCenter.title?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.name?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.id === 'torino';
    return isSicilia ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  };

  const availableLanguages = getAvailableLanguages();
  const descriptionEn = Form.useWatch('description_en', form);
  const descriptionFr = Form.useWatch('description_fr', form);

  useEffect(() => {
    if (isEditMode) {
      const load = async () => {
        setLoading(true);
        try {
          const item = await fetchItem(id);
          if (item) {
            setInitialData(item);
            const shoppingCenterId = item.shopping_center?.id || item.shopping_center_id;
            
            const center = shoppingCenters.find(
              (c) => (c.value || c.id) === shoppingCenterId
            );
            if (center) {
              setSelectedShoppingCenter(center);
            }
            
            form.setFieldsValue({
              title_it: item.title_it || item.title,
              title_en: item.title_en,
              title_fr: item.title_fr,
              description_it: item.description_it || item.description,
              description_en: item.description_en,
              description_fr: item.description_fr,
              description_short_it: item.description_short_it || item.description_short,
              description_short_en: item.description_short_en,
              description_short_fr: item.description_short_fr,
              shopping_center_id: shoppingCenterId,
            });
            if (item.image?.url) {
              setFileList([{ uid: '-1', name: item.image.file_name || 'image.jpg', status: 'done', url: item.image.url }]);
            }
          }
        } catch (e) {
          message.error(t('Failed to load itinerary'));
        } finally {
          setLoading(false);
        }
      };
      load();
    }
  }, [id, isEditMode, fetchItem, form, shoppingCenters]);

  const handleSubmit = useCallback(async (values) => {
    setSubmitAttempted(true);
    try {
      if (!values.title_it || !values.description_it || !values.shopping_center_id) {
        message.error(t('I campi in italiano sono obbligatori'));
        return;
      }
      if (!isEditMode) {
        const hasImage = fileList.length > 0 && (fileList[0].originFileObj || fileList[0].url);
        if (!hasImage) {
          message.error(t('L\'immagine è obbligatoria'));
          return;
        }
      }
      const payload = {
        title_it: values.title_it,
        description_it: values.description_it,
        description_short_it: values.description_short_it,
        shopping_center_id: values.shopping_center_id,
        title_en: values.title_en,
        title_fr: values.title_fr,
        description_en: values.description_en,
        description_fr: values.description_fr,
        description_short_en: values.description_short_en,
        description_short_fr: values.description_short_fr,
      };
      if (fileList.length > 0 && fileList[0].originFileObj) payload.image = fileList[0].originFileObj;
      let result;
      if (isEditMode) result = await updateItem(id, payload); else result = await createItem(payload);
      if (result) {
        message.success(isEditMode ? t('Itinerary aggiornato') : t('Itinerary creato'));
        navigate('/website/itineraries');
      }
    } catch (e) {}
  }, [isEditMode, id, createItem, updateItem, fileList, navigate, t]);

  const uploadProps = {
    beforeUpload: () => false,
    fileList,
    onChange: ({ fileList: newFileList }) => setFileList(newFileList),
    onRemove: () => setFileList([]),
    accept: 'image/*',
    maxCount: 1,
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}><Spin size='large' /></div>;
  }

  const editorInit = {
    height: 300,
    menubar: false,
    paste_data_images: true,
    plugins: ['advlist autolink lists link image', 'charmap preview anchor', 'searchreplace visualblocks code', 'insertdatetime media table paste wordcount'],
    toolbar: 'undo redo | styleselect | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image | removeformat',
    block_formats: 'Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3',
    forced_root_block: 'p',
    invalid_styles: 'width,height',
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px } img {max-width:100%; height: auto;}',
    file_picker_types: 'image',
    file_picker_callback: (cb) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.onchange = function () {
        const file = this.files[0];
        const reader = new FileReader();
        reader.onload = function () { cb(reader.result, { title: file.name }); };
        reader.readAsDataURL(file);
      };
      input.click();
    },
  };

  return (
    <Card title={isEditMode ? t('Modifica Itinerary') : t('Crea Itinerary')} extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/website/itineraries')}>{t('Indietro')}</Button>}>
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16, fontWeight: 500, color: '#666' }}>{t('Contenuto')}</div>
      {/* Outlet Village */}
      <Form.Item name='shopping_center_id' label={t('Outlet Village')} rules={[{ required: true, message: t('Seleziona un Outlet Village') }]}>
          <Select placeholder={t('Seleziona Outlet Village')} loading={loadingCompanies} showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())} onChange={(value) => { const center = shoppingCenters.find((c) => (c.value || c.id) === value); setSelectedShoppingCenter(center); }}>
            {shoppingCenters.filter((center) => center && (center.value || center.id) && (center.title || center.name)).map((center) => (
              <Option key={center.value || center.id} value={center.value || center.id}>{center.title || center.name}</Option>
            ))}
          </Select>
        </Form.Item>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='title_it'
                label={
                  <TranslateFormLabel
                    label={`${t('Titolo')} IT`}
                    form={form}
                    sourceField='title_it'
                    textKey='title'
                    targetFields={{ en: 'title_en', fr: 'title_fr' }}
                  />
                }
                rules={[{ required: true, message: t('Il titolo in italiano è obbligatorio') }]}
              >
                <Input placeholder={t('Inserisci il titolo')} />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='title_en' label={`${t('Titolo')} EN`}>
                <Input placeholder={t('Inserisci il titolo')} />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='title_fr' label={`${t('Titolo')} FR`}>
                  <Input placeholder={t('Inserisci il titolo')} />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Descriptions one per row */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name='description_it' label={`${t('Descrizione')} IT`} hidden>
                <Input.TextArea />
              </Form.Item>
              <Form.Item
                label={
                  <TranslateFormLabel
                    label={`${t('Descrizione')} IT`}
                    form={form}
                    sourceField='description_it'
                    textKey='description'
                    targetFields={{
                      en: 'description_en',
                      fr: 'description_fr',
                    }}
                  />
                }
                colon={false}
              >
                <Editor apiKey='y4qiwpjqismb8n0ox89rpjv9gort076zw0yviiamon1rtdps' initialValue={initialData ? (initialData.description_it || initialData.description || '') : ''} init={editorInit} onEditorChange={(c) => form.setFields([{ name: 'description_it', value: c }])} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name='description_en' label={`${t('Descrizione')} EN`} hidden>
                <Input.TextArea />
              </Form.Item>
              <Form.Item label={`${t('Descrizione')} EN`} colon={false}>
                <Editor apiKey='y4qiwpjqismb8n0ox89rpjv9gort076zw0yviiamon1rtdps' value={descriptionEn ?? ''} init={editorInit} onEditorChange={(c) => form.setFields([{ name: 'description_en', value: c }])} />
              </Form.Item>
            </Col>
          </Row>
          {availableLanguages.includes('FR') && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name='description_fr' label={`${t('Descrizione')} FR`} hidden>
                  <Input.TextArea />
                </Form.Item>
                <Form.Item label={`${t('Descrizione')} FR`} colon={false}>
                  <Editor apiKey='y4qiwpjqismb8n0ox89rpjv9gort076zw0yviiamon1rtdps' value={descriptionFr ?? ''} init={editorInit} onEditorChange={(c) => form.setFields([{ name: 'description_fr', value: c }])} />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>

        {/* Short descriptions */}
        <Row gutter={16}>
          <Col span={availableLanguages.length === 3 ? 8 : 12}>
            <Form.Item
              name='description_short_it'
              label={
                <TranslateFormLabel
                  label={`${t('Descrizione corta')} IT`}
                  form={form}
                  sourceField='description_short_it'
                  textKey='description_short'
                  targetFields={{
                    en: 'description_short_en',
                    fr: 'description_short_fr',
                  }}
                />
              }
            >
              <Input.TextArea
                placeholder={t('Inserisci descrizione breve')}
                rows={3}
              />
            </Form.Item>
          </Col>
          <Col span={availableLanguages.length === 3 ? 8 : 12}>
            <Form.Item name='description_short_en' label={`${t('Descrizione corta')} EN`}>
              <Input.TextArea 
                placeholder={t('Inserisci descrizione breve')} 
                rows={3}
              
               
              />
            </Form.Item>
          </Col>
          {availableLanguages.includes('FR') && (
            <Col span={8}>
              <Form.Item name='description_short_fr' label={`${t('Descrizione corta')} FR`}>
                <Input.TextArea 
                  placeholder={t('Inserisci descrizione breve')} 
                  rows={3}
              
                />
              </Form.Item>
            </Col>
          )}
        </Row>

  

        {/* Image */}
        <Form.Item name='image' label={t('Immagine')}>
          <Upload.Dragger {...uploadProps} style={{ width: '100%' }}>
            <p className='ant-upload-drag-icon'><InboxOutlined /></p>
            <p className='ant-upload-text'>{t('Clicca o trascina un file in questa area per caricarlo')}</p>
            <p className='ant-upload-hint'>{t('Supporta solo file immagine')}</p>
          </Upload.Dragger>
        </Form.Item>
        {submitAttempted && fileList.length === 0 && !isEditMode && (
          <div style={{ color: '#ff4d4f', fontSize: 14, marginTop: -12, marginBottom: 10 }}>{t('Non può essere lasciato in bianco')}</div>
        )}

        <Form.Item>
          <Button type='primary' width={'150px'} big htmlType='submit' loading={loading} icon={<SaveOutlined />} style={{ backgroundColor: '#002637', borderColor: '#002637' }}>
            {isEditMode ? t('Aggiorna') : t('Crea')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
});




